import { useState } from 'react';
import { useMutation } from '@apollo/client/react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  InputAdornment,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { Groups } from '@mui/icons-material';
import { IdentityProviderSummaryItemFragment } from '../__generated__/graphql';
import { GET_SINGLE_ADFS_BY_BKS_ID } from '../administration/ADFSConfig/ADFSConfig.graphql';
import { GET_IDENTITY_PROVIDER_SUMMARY, UPDATE_SEAT_LIMIT } from './Sso.graphql';
import { SsoField, SsoSection } from './SsoSection';

interface Props {
  summary: IdentityProviderSummaryItemFragment;
}

const explanation = (
  <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
    Gjelder både innlogging og brukersynkronisering, og lisensen vurderes samlet. Uten begrensning kan den få flere brukere enn den har kjøpt. Med
    begrensning slipper nye brukere inn så lenge minst ett abonnement har et ledig sete, eller brukerne på lisensen er færre enn det største antallet
    kjøpte seter pluss ekstra prosent. Den som allerede har tilgang på lisensen, slipper alltid inn.
  </Typography>
);

function SeatTable({ summary }: Props) {
  const subscriptions = (summary.subscriptions ?? []).filter((x): x is NonNullable<typeof x> => !!x);
  return (
    <Table size="small" sx={{ maxWidth: 720 }}>
      <TableHead>
        <TableRow>
          <TableCell>Abonnement</TableCell>
          <TableCell align="right">Kjøpte seter</TableCell>
          <TableCell align="right">Brukere</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {subscriptions.map((x) => {
          const over = x.bought !== null && x.bought !== undefined && x.inUse > x.bought;
          return (
            <TableRow key={x.subscriptionId}>
              <TableCell>{x.name ?? `Abonnement ${x.subscriptionId}`}</TableCell>
              <TableCell align="right">{x.bought ?? '—'}</TableCell>
              <TableCell align="right" sx={{ color: over ? 'warning.main' : undefined, fontWeight: over ? 500 : undefined }}>
                {x.inUse}
                {over && ' (over kjøpt)'}
              </TableCell>
            </TableRow>
          );
        })}
        <TableRow sx={{ '& td': { fontWeight: 500, borderBottom: 0 } }}>
          <TableCell>Lisensen samlet (unike brukere)</TableCell>
          <TableCell align="right">
            {summary.seatsBought}
            {summary.isRestricted && summary.extraUserPercentage > 0 && ` + ${summary.extraUserPercentage} % = ${summary.seatLimit}`}
          </TableCell>
          <TableCell align="right" sx={{ color: summary.isRestricted && !summary.hasRoomForNewUser ? 'error.main' : undefined }}>
            {summary.seatsInUse}
          </TableCell>
        </TableRow>
      </TableBody>
    </Table>
  );
}

export default function SsoSeatsSection({ summary }: Props) {
  const [editing, setEditing] = useState(false);
  const [restricted, setRestricted] = useState(summary.isRestricted);
  const [extra, setExtra] = useState(String(summary.extraUserPercentage));
  const [error, setError] = useState<string | null>(null);

  const [update, { loading }] = useMutation(UPDATE_SEAT_LIMIT, {
    refetchQueries: [
      { query: GET_IDENTITY_PROVIDER_SUMMARY, variables: { bksID: summary.bksID } },
      { query: GET_SINGLE_ADFS_BY_BKS_ID, variables: { bksID: summary.bksID } },
    ],
    awaitRefetchQueries: true,
  });

  const extraNumber = Number(extra);
  const extraValid = extra.trim() !== '' && Number.isInteger(extraNumber) && extraNumber >= 0 && extraNumber <= 200;

  const startEditing = () => {
    setRestricted(summary.isRestricted);
    setExtra(String(summary.extraUserPercentage));
    setError(null);
    setEditing(true);
  };

  const save = async () => {
    setError(null);
    try {
      await update({
        variables: {
          input: { bksID: summary.bksID, isRestricted: restricted, extraUserPercentage: extraValid ? extraNumber : summary.extraUserPercentage },
        },
      });
      setEditing(false);
    } catch (e: any) {
      setError(e?.message ?? 'Kunne ikke lagre.');
    }
  };

  return (
    <SsoSection
      title="Seter"
      icon={<Groups fontSize="small" />}
      action={
        !editing && (
          <Button size="small" variant="outlined" onClick={startEditing}>
            Rediger
          </Button>
        )
      }
    >
      {!editing ? (
        <>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 2, mb: 2 }}>
            <SsoField label="Begrenset til kjøpte seter" value={summary.isRestricted ? 'Ja' : 'Nei'} />
            <SsoField label="Ekstra brukere" value={summary.isRestricted ? `${summary.extraUserPercentage} %` : 'Ikke i bruk'} />
            <SsoField
              label="Plass til nye brukere"
              value={!summary.isRestricted ? 'Ja, ingen grense' : summary.hasRoomForNewUser ? 'Ja' : 'Nei, lisensen er full'}
            />
          </Box>
          <SeatTable summary={summary} />
          {explanation}
        </>
      ) : (
        <Stack spacing={1}>
          <Stack direction="row" spacing={3} alignItems="flex-start" flexWrap="wrap" useFlexGap>
            <FormControlLabel
              control={<Checkbox checked={restricted} onChange={(e) => setRestricted(e.target.checked)} />}
              label="Begrens til kjøpte seter"
            />
            <TextField
              label="Ekstra brukere"
              size="small"
              type="number"
              value={extra}
              disabled={!restricted}
              onChange={(e) => setExtra(e.target.value)}
              error={restricted && !extraValid}
              helperText={restricted && !extraValid ? 'Et helt tall fra 0 til 200' : undefined}
              sx={{ width: 160 }}
              slotProps={{
                input: { endAdornment: <InputAdornment position="end">%</InputAdornment> },
                htmlInput: { min: 0, max: 200, step: 1 },
              }}
            />
          </Stack>
          {explanation}
          {error && <Alert severity="error">{error}</Alert>}
          <Stack direction="row" spacing={1} sx={{ pt: 1 }}>
            <Button variant="contained" disabled={loading || (restricted && !extraValid)} onClick={save}>
              Lagre
            </Button>
            <Button disabled={loading} onClick={() => setEditing(false)}>
              Avbryt
            </Button>
          </Stack>
        </Stack>
      )}
    </SsoSection>
  );
}
